import { useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';

type UseStaffMFARequirement = {
  organizationName: string;
  canEdit: boolean;
  isMFASettingsEnabled: boolean;
  isRequiredForEveryone: boolean;
  isConfirmationOpen: boolean;
  isSaving: boolean;
  requestOrganizationRequirement: (required: boolean) => void;
  confirmOrganizationRequirement: () => Promise<void>;
  cancelOrganizationRequirement: () => void;
  setStaffRequirement: (staffID: string, required: boolean) => Promise<void>;
};

/**
 * Owner-side control of the organization's two-step verification policy.
 * Turning it on for everyone asks for confirmation first; turning it off and
 * the per-member switches apply straight away.
 *
 * Every write swallows its rejection because the store's error middleware
 * already raises the toast; a failed write leaves the confirmation open.
 */
export const useStaffMFARequirement = (): UseStaffMFARequirement => {
  const dispatch = useAppDispatch();

  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Rapid toggling would otherwise settle on whichever response lands last.
  const savingStaffIDsRef = useRef(new Set<string>());

  const organization = useAppSelector(
    organizationModel.selectors.selectCurrentOrganization,
  );
  const isMFASettingsEnabled = useAppSelector(
    organizationModel.selectors.selectIsMFASettingsEnabled,
  );

  const organizationID = organization?.ID ?? '';
  const canEdit = organization?.YourPermissions?.MFACanEdit ?? false;
  const isRequiredForEveryone =
    organization?.Settings?.MFARequireForAllMembers ?? false;

  useEffect(() => {
    if (!isMFASettingsEnabled) {
      setIsConfirmationOpen(false);
    }
  }, [isMFASettingsEnabled]);

  const applyOrganizationRequirement = async (
    required: boolean,
  ): Promise<boolean> => {
    setIsSaving(true);

    try {
      await dispatch(
        organizationModel.thunks.setOrganizationMFARequirement({
          OrganizationID: organizationID,
          Required: required,
        }),
      ).unwrap();

      return true;
    } catch {
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const requestOrganizationRequirement = (required: boolean) => {
    if (required) {
      setIsConfirmationOpen(true);
      return;
    }

    applyOrganizationRequirement(false);
  };

  const confirmOrganizationRequirement = async () => {
    if (await applyOrganizationRequirement(true)) {
      setIsConfirmationOpen(false);
    }
  };

  const setStaffRequirement = async (staffID: string, required: boolean) => {
    if (savingStaffIDsRef.current.has(staffID)) {
      return;
    }

    savingStaffIDsRef.current.add(staffID);

    try {
      await dispatch(
        organizationModel.thunks.setOrganizationStaffMFARequirement({
          OrganizationID: organizationID,
          StaffID: staffID,
          Required: required,
        }),
      ).unwrap();
    } catch {
      // Reported by the error middleware; the switch reverts on the next update.
    } finally {
      savingStaffIDsRef.current.delete(staffID);
    }
  };

  return {
    organizationName: organization?.Name ?? '',
    canEdit,
    isMFASettingsEnabled,
    isRequiredForEveryone,
    isConfirmationOpen,
    isSaving,
    requestOrganizationRequirement,
    confirmOrganizationRequirement,
    cancelOrganizationRequirement: () => setIsConfirmationOpen(false),
    setStaffRequirement,
  };
};
